import { useObserver } from 'mobx-react-lite';
import { OptimizelyProduct, Source, Destination, Target } from '../types/DataSyncItem';
import { DataSyncObjectsStore } from '../stores/object/DataSyncObjectsStore';
import ProductStoreFactory from '../stores/product/ProductStoreFactory';

interface InstanceAvailabilityParams {
  product: OptimizelyProduct;
  instanceId?: string;
  target: Target;
}

type InstanceAvailabilityStatus = 'loading' | 'available' | 'unavailable';

interface ObjectAvailabilityParams {
  objectName?: string;
  objectStore: DataSyncObjectsStore | null;
  target: Target;
}

export function useInstanceAvailability(params: InstanceAvailabilityParams): InstanceAvailabilityStatus {
  const { product, instanceId, target } = params;

  return useObserver(() => {
    if (!product || !instanceId) {
      return 'available';
    }

    const productStore = target === Target.SOURCE
      ? ProductStoreFactory.getSourceProductStore()
      : ProductStoreFactory.getDestinationProductStore();

    const isLoading = product === OptimizelyProduct.HUB_APP
      ? productStore.hubAppsLoading
      : productStore.productInstancesLoading;

    if (isLoading) {
      return 'loading';
    }

    const available = productStore.isInstanceAvailable(product, instanceId);
    return available ? 'available' : 'unavailable';
  });
}

/**
 * Hook to check if an object is available
 * Returns true if object is unavailable (for UI warning display)
 */
export function useObjectAvailability(params: ObjectAvailabilityParams): boolean {
  const { objectName, objectStore, target } = params;

  return useObserver(() => {
    if (!objectName || !objectStore) {
      return false;
    }

    if (objectStore.loading) {
      return false;
    }

    const isAvailable = target === Target.SOURCE
      ? objectStore.isSourceObjectAvailable(objectName)
      : objectStore.isDestinationObjectAvailable(objectName);

    return !isAvailable;
  });
}

/**
 * Combined hook for checking both instance and object availability
 * Returns object with instanceUnavailable and objectUnavailable flags
 */
export function useSourceAvailability(
  source: Source,
  dataSyncObjectsStore: DataSyncObjectsStore | null
) {
  const instanceStatus = useInstanceAvailability({
    product: source.product,
    instanceId: source.instance?.id,
    target: Target.SOURCE,
  });

  const objectUnavailable = useObjectAvailability({
    objectName: source.object?.object_name,
    objectStore: dataSyncObjectsStore,
    target: Target.SOURCE,
  });

  return {
    instanceUnavailable: instanceStatus === 'unavailable',
    objectUnavailable
  };
}

/**
 * Combined hook for checking both instance and object availability for destination
 * Returns object with instanceUnavailable and objectUnavailable flags
 */
export function useDestinationAvailability(
  destination: Destination,
  dataSyncObjectsStore: DataSyncObjectsStore | null
) {
  const instanceStatus = useInstanceAvailability({
    product: destination.product,
    instanceId: destination.instance?.id,
    target: Target.DESTINATION,
  });

  const objectUnavailable = useObjectAvailability({
    objectName: destination.object?.object_name,
    objectStore: dataSyncObjectsStore,
    target: Target.DESTINATION,
  });

  return {
    instanceUnavailable: instanceStatus === 'unavailable',
    objectUnavailable
  };
}
